import React from 'react';
import { connect } from 'react-redux';

import EligibilityOverview from '../../../components/individualPortal/eligibilityResults/EligibilityOverview';
import Tooltip from '../../../components/common/tooltip/Tooltip';
import { ALL_ELIGIBILITY_TYPES } from '../../../constants/individualPortal/dashboard/dashboardModelTypes';
import { renderAmount } from '../../../utils/dashboard/processCommon';
import { FormattedMessage } from 'react-intl/dist';
import { capitalize, uniq } from '../../../utils/miscellaneous/objectHelpers';

const EligibilityOverviewContainer = props => {
    const {
        eligibilityResults: {
            applicationDate,
            applicationYear,
            caseNumber,
            isFinancial,
            stateSubsidy,
            taxHouseholdDetails
        },
        environment: {
            isState
        },
        processedEligibilityData,
        additionalNote,
        showSummaryHeader
    } = props;

    /**
     * Helper function, takes aptc, name from taxHouseholdGroup and stateSubsidy from eligibilityResults.
     * @returns {object of replacement parameters for i18n}
     * @param getReplacements
    //  */


    const getReplacements = (aptc, stateSubsidy, name) => ({
        aptcTooltip: <Tooltip
            id='household_section.eligibility.status.eligible_aptc'
            content='household_section.tooltip_aptc'
            index='eligibility_overview'
        />,
        stateSubsidyTooltip: <Tooltip
            id='household_section.eligibility.status.eligible_state_subsidy'
            content='household_section.tooltip_state_subsidy'
            index='eligibility_overview'
            dataAutomationId='state_subsidy_eligibility_overview'
        />,
        csrTooltip: <Tooltip
            id='household_section.eligibility.status.csr'
            content='household_section.tooltip_csr'
            index='eligibility_overview'
        />,
        coinsuranceTooltip: <Tooltip
            id='common.coinsurance'
            content='household_section.tooltip_coinsurance'
            index='eligibility_overview'
        />,
        copaymentTooltip: <Tooltip
            id='common.copayment'
            content='household_section.tooltip_copayment'
            index='eligibility_overview'
        />,
        deductibleTooltip: <Tooltip
            id='common.deductible'
            content='household_section.tooltip_deductible'
            index='eligibility_overview'
        />,
        aptcAmount: renderAmount(aptc),
        stateSubsidyAmount: renderAmount(stateSubsidy),
        members: name
    });

    /**
     * Main logic for EligibilityOverview component.
     * @param data
     * @returns {*}
     */
    const getEligibilityOverview = data => {
        if (!data) return false;

        const { QHP, APTC, STATE_SUBSIDY, CSR, ASSESSED_MEDICAID, ASSESSED_CHIP, MEDICAID_OR_CHIP } = ALL_ELIGIBILITY_TYPES;

        const processedApplicantInfo = data.reduce((acc, item) => {
            if (!acc[item.rawData.taxHouseholdGroup]) {
                acc[item.rawData.taxHouseholdGroup] = { aptc: 0, eligibilities: [], name: '' };
            }
            let memberName = '';
            /*Only if taxHouseholdGroup is not 'NON_ITF' print the applicant who is seeking coverage in the taxHouseholdGroup */
            if( item.rawData.taxHouseholdGroup !== 'NON_ITF' && item.rawData.seekingCoverage && item.rawData.firstName && item.rawData.lastName ){
                memberName = capitalize(item.rawData.firstName) + ' ' + capitalize(item.rawData.lastName);
            }else{
                if(item.rawData.taxHouseholdGroup === 'NON_ITF') {
                    memberName = 'members';
                }

            }
            if(item.rawData.seekingCoverage) {
                acc[item.rawData.taxHouseholdGroup] = {
                    name: memberName,
                    aptc: acc[item.rawData.taxHouseholdGroup].aptc + item.rawData.aptc,
                    eligibilities: [ ...acc[item.rawData.taxHouseholdGroup].eligibilities, ...item.eligibilities ]
                };
            }

            return acc;
        }, {});

        Object.keys(processedApplicantInfo).forEach((key) => {
            processedApplicantInfo[key].eligibilities = uniq(processedApplicantInfo[key].eligibilities);
        });

        // Order of eligibilities which should be displayed for financial and non financial application.
        const ELIGIBILITY_FOR_OVERVIEW_NON_FINANCIAL = [QHP, CSR];
        const ELIGIBILITY_FOR_OVERVIEW_FINANCIAL = [QHP, APTC, STATE_SUBSIDY, CSR, ASSESSED_MEDICAID, ASSESSED_CHIP, MEDICAID_OR_CHIP];
        const ELIGIBILITY_FOR_OVERVIEW = isFinancial ? ELIGIBILITY_FOR_OVERVIEW_FINANCIAL : ELIGIBILITY_FOR_OVERVIEW_NON_FINANCIAL;

        const ELIGIBILITY_OVERVIEW_KEYS = {
            QHP: 'eligibility_results.overview.qhp',
            APTC: 'eligibility_results.overview.aptc',
            STATE_SUBSIDY: 'eligibility_results.overview.state_subsidy',
            CSR: 'eligibility_results.overview.csr',
            ASSESSED_MEDICAID: 'eligibility_results.overview.assessed_medicaid',
            ASSESSED_CHIP: 'eligibility_results.overview.assessed_chip',
            MEDICAID_OR_CHIP: 'eligibility_results.overview.assessed_medicaid_or_chip'
        };

        let eligibilityList = {};
        Object.entries(processedApplicantInfo).forEach(([key, value]) => {

            let programArray = [];
            value.eligibilities.length > 0 && value.eligibilities.forEach(program => {
                if ((program !== 'NOT_QHP' || program !== 'NOT_SEEKING' ) &&  ELIGIBILITY_FOR_OVERVIEW.includes(program)) {
                    programArray.push(
                        <FormattedMessage
                            key={program}
                            id={ELIGIBILITY_OVERVIEW_KEYS[program]}
                            values={getReplacements(value.aptc, stateSubsidy, value.name)}
                        />
                    );
                }
            });
            eligibilityList = { ...eligibilityList, [key]: programArray };
        });

        const orderedEligibilityList = Object.entries(eligibilityList).reduce((newObj, [key, value]) => {
            if(value.length) {
                newObj[key] = value;
            }
            return newObj;
        }, {});
        return {
            orderedEligibilityList,
            processedApplicantInfo
        };
    };
    
    return (
        <EligibilityOverview
            applicationDate={applicationDate}
            caseNumber={caseNumber}
            coverageYear={applicationYear}
            data={getEligibilityOverview(processedEligibilityData)}
            isFinancial={isFinancial}
            isState={isState}
            additionalNote={additionalNote}
            showSummaryHeader={showSummaryHeader}
            taxHouseholdDetails={taxHouseholdDetails}
        />
    );
}; 
const mapStateToProps = state => ({
    eligibilityResults: state.individualPortal.eligibilityResults,
    environment: state.common.environment
});

export default connect(mapStateToProps)(EligibilityOverviewContainer);